/**
 * Capital Stack tab model — recorded layers from the deal, completed by a
 * residual equity slice only when a stored figure implies one: the total
 * equity, the uses the deal states (a purchase price, a project cost, a
 * schedule), or the property value. That residual is labeled implied; it is
 * never written back as a tranche. A stated LTV never implies one: loan ÷ LTV
 * is the value back-solve rule R3 forbids, and it is not shown even labelled
 * Implied (Round 3 ruling; nd3-06 / nd3-31).
 *
 * Why the uses come before the value (Oct 2026 redesign). A capital stack is
 * how a deal is PAID FOR: debt plus the sponsor's cash equals what the deal
 * costs. On an acquisition that is the purchase price and its costs, which the
 * package nearly always states, while an as-is value is often not stated at
 * all. Reading only the value left Washington Square — $132M against a stated
 * $176M purchase price — as a lone senior tranche, and Sources & Uses called
 * the sponsor's $44M "Uses exceed sources by $44,000,000", an error, when it
 * is simply the equity no document states. Value minus debt is equity in the
 * asset, which is a different question (the LTV answers it), so the value now
 * closes the stack only when no cost is stated — a refinance.
 */

import type { RawDealFile } from '@/services/dealFile'
import type { LoanActivityEvent } from '@/pages/pipeline-intelligence/types'
import type { DealDocument } from '@/services/documents'
import type { Covenant, LoanStructure } from '@/services/structures'
import { asNumber } from './format'
import { amortizationLabel, productField, productLabel, seniorAheadUnstated, stackPosition } from './dealTerms'
import { heldBasis, heldFor, heldRevisions } from './heldRevisions'
import { humanizeEvent } from './workspace/activityNarrative'

export type LayerKind = 'senior' | 'mezz' | 'pref' | 'equity' | 'other'

export type StackLayer = {
  id: string
  kind: LayerKind
  position: string
  lender: string | null
  amount: number
  rate: number | undefined
  rateLabel: string | null
  amortization: string | null
  term: string | null
  status: string | null
  isDebt: boolean
  /** True only when the stored layer itself, or the recorded loan request,
   * identifies this tranche as the request. Never inferred from layer order. */
  isRequested: boolean
  /** True when this slice was derived from a recorded property value rather
   * than stored as a tranche. */
  isImplied: boolean
  /** The catalogue field this layer's amount IS (the recorded request's
   *  loan_amount), so the figure carries that field's value state (C05). */
  amountField?: string
  /** The formula of an amount this model computed (a future-funding leg is
   *  the commitment less the loan): it reads Derived, never as stated. */
  amountDerived?: string
  /** The catalogue fields a computed amount is made of, so an unlocked
   *  dossier opens them in place of the figure (R1). */
  amountInputs?: string[]
  /** The catalogue field this layer's rate was read from. */
  rateField?: string
  /** Why an unlocked dossier cannot edit this tranche in place: a stored
   *  capital-stack record is not a catalogue field (no field, no column). */
  readOnlyReason?: string
}

/** A tranche read from the deal's stored capital stack. No dossier editor
 *  writes that record, so its figures read as stored, and say why. */
export const STORED_TRANCHE_REASON =
  'A tranche from the deal’s stored capital stack. It is not a catalog field, so it is not edited in place.'

/** A line of a stored sources & uses schedule (a sponsor workbook's S&U). */
export const STORED_SCHEDULE_REASON =
  'A line of the deal’s stored sources & uses schedule. It is not a catalog field, so it is not edited in place.'

/** What closed the stack when no equity tranche is stored: the recorded total
 *  equity, the stated uses (`uses`: a purchase price, a project cost or a
 *  schedule, less the debt), or the recorded property value less the debt. */
export type ResidualSource = 'equity_field' | 'uses' | 'property_value'

/** The formulas behind the bar's Total Equity when the page computed it
 *  rather than read it: one closing the gap to the stated uses or a recorded
 *  value makes the equity a remainder. A recorded equity is not computed. */
export function derivedTotals(source: ResidualSource | null): { totalCap?: string; totalEquity?: string } {
  if (source === 'property_value') return { totalEquity: 'recorded value − recorded debt' }
  if (source === 'uses') return { totalEquity: 'total uses − total debt' }
  return {}
}

/** The formula behind the Sponsor Equity Summary's equity when no document
 *  states it: the gap to a recorded value the strip's Total Equity marks,
 *  else the engine's own derivation of total equity. */
export function sponsorEquityFormula(input: {
  residualSource: ResidualSource | null
  equityDerived?: boolean
  equityFormula?: string | null
}): string | undefined {
  const implied = derivedTotals(input.residualSource).totalEquity
  if (implied) return implied
  return input.equityDerived ? input.equityFormula ?? 'total cost − loan' : undefined
}

/** How the GP + LP gap note qualifies the total equity it is measured
 *  against: a derived total is never "as stated" (nd-016). */
export function equityGapBasis(derivedFormula: string | undefined): string {
  return derivedFormula
    ? `the total equity is derived (${derivedFormula}), not a stated figure.`
    : 'as stated.'
}

/** One line of Sources & Uses. `implied` marks the equity plug the stack
 *  closed with — uses less debt — which no document states. */
export type NamedAmount = {
  label: string
  amount: number
  implied?: boolean
  /** The catalogue field the line IS, when it was read from one (not a stored
   *  schedule), so it carries that field's state and whose statement it is. */
  field?: string
  /** For a line no catalogue field holds — a stored schedule's, or one read
   *  off the loan structure or the cash-flow record — where it was read from,
   *  so an unlocked dossier says why it is not an input. */
  readOnlyReason?: string
  /** The formula of a line this model computed — the implied equity plug, or
   *  a future-funding leg — as the stack's own segment states it. An equity
   *  the value closed is "value (as-is) − recorded debt", never the uses
   *  formula a Sources row used to print for every implied line. */
  formula?: string
  /** The catalogue fields a computed line is made of, so an unlocked dossier
   *  opens them in place of the figure (R1). */
  inputs?: string[]
}

/** The formula of the implied equity that closes a stack, by what closed it:
 *  the stated uses, or the value (less the debt at closing when future
 *  advances are drawn beyond it). One wording for the bar, the Overview card
 *  and Sources & Uses. */
export function impliedEquityFormula(input: {
  residualSource: ResidualSource | null
  usesBasis: string | null
  beyondValue: boolean
}): string {
  if (input.residualSource === 'uses') {
    return input.usesBasis === 'the purchase price'
      ? 'purchase price − loan'
      : input.usesBasis === 'the total project cost'
        ? 'total project cost − total debt'
        : 'total uses − total debt'
  }
  // Future funding is advanced after closing: the value less the debt AT
  // closing, not less every advance.
  return input.beyondValue ? 'value (as-is) − debt at closing' : 'value (as-is) − recorded debt'
}

/** The catalogue inputs an implied equity is made of: the uses the deal
 *  states (or the value) and the debt. */
export function impliedEquityInputs(input: {
  residualSource: ResidualSource | null
  layers: StackLayer[]
  uses: NamedAmount[]
  totalCost: number | undefined
}): string[] {
  if (input.residualSource === 'uses') {
    // Uses pay for the whole stack, future advances included.
    const advances = input.layers.flatMap((layer) => layer.amountInputs ?? [])
    const used = input.uses.map((row) => row.field).filter((name): name is string => Boolean(name))
    return [...new Set([...used, ...(input.totalCost != null ? ['total_cost'] : []), 'loan_amount', ...advances])]
  }
  // The value is measured at closing: the advances are not in it.
  return ['property_value', 'loan_amount']
}

/** A covenant limit held off the deal for a person to decide (a later
 *  email's, or the lender's own draft). Proposed only: it carries no
 *  compliance, because it is not the deal's covenant until someone applies it. */
export type ProposedCovenant = {
  id: string
  name: string
  requirement: string
  /** Whose it is and where it waits: "lender draft (INQ-…), awaiting review". */
  held: string
}

/** What a covenant measures, so a page can draw the deal's figure against the
 *  limit: the metric, the limit as stated and the deal's own figure today —
 *  both on the scale the row's text prints (points for a percent, a multiple
 *  for DSCR) — and which side of the limit is compliant. Carried only when the
 *  record states a number for both the metric and the limit's side. */
export type CovenantGauge = {
  metric: 'ltv' | 'dscr' | 'debt_yield' | 'occupancy'
  limit: number
  value: number | undefined
  direction: 'min' | 'max'
}

export type CovenantRow = {
  id: string
  name: string
  requirement: string
  current: string
  compliant: boolean | null
  gauge?: CovenantGauge
}

export type StackActivityRow = {
  id: string
  at: string
  title: string
  party: string
  detail: string
  status: string | null
}

function num(v: unknown): number | undefined {
  return asNumber(v)
}

/** A cash-flow amount, in the DOLLARS every deal stores. Anything under 500
 *  used to be read as $MM (a $250 reserve became $250M on Sources & Uses); a
 *  unit is never guessed from a figure's size (CMP-08). */
function moneyDollars(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n === 0) return undefined
  return n
}

function parseRate(value: unknown): number | undefined {
  if (value == null || value === '') return undefined
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value <= 1.5 ? value * 100 : value
  }
  const text = String(value).replace('%', '').trim()
  const n = Number(text)
  if (!Number.isFinite(n) || n === 0) return undefined
  return n <= 1.5 ? n * 100 : n
}

function classify(label: string): LayerKind {
  const t = label.toLowerCase()
  if (/\bmezz|mezzanine|b-?note|b note\b/.test(t)) return 'mezz'
  if (/\bpref|preferred equity|preferred\b/.test(t)) return 'pref'
  if (/\bequity|sponsor|gp\b|lp\b|common\b/.test(t)) return 'equity'
  if (/\bsenior|first mortgage|perm|bridge|construction|loan\b/.test(t)) return 'senior'
  return 'other'
}

function classifyOr(label: string, fallback: LayerKind): LayerKind {
  const kind = classify(label)
  return kind === 'other' ? fallback : kind
}

function isDebtKind(kind: LayerKind): boolean {
  return kind === 'senior' || kind === 'mezz' || kind === 'other'
}

function isTruthyFlag(value: unknown): boolean {
  if (value === true) return true
  if (typeof value !== 'string') return false
  return ['true', 'yes', 'requested', 'request'].includes(value.trim().toLowerCase())
}

function sameMoney(left: number, right: number): boolean {
  return Math.abs(left - right) <= Math.max(1, Math.abs(right) * 0.000001)
}

function cashBlob(raw: RawDealFile | undefined): Record<string, unknown> {
  const direct = raw?.cash_flows
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct as Record<string, unknown>
  }
  const custom = raw?.custom_fields
  if (custom && typeof custom === 'object') {
    const cash = (custom as Record<string, unknown>).cash_flows
    if (cash && typeof cash === 'object' && !Array.isArray(cash)) {
      return cash as Record<string, unknown>
    }
  }
  return {}
}

/** A schedule line that is the side's own total ("Total Uses", "Total Sources
 *  of Funds", "Total Project Cost", "Total Capitalization"). */
const SIDE_TOTAL_RE =
  /^(?:grand\s+)?total(?:\s+(?:sources?|uses?|sources?\s*(?:&|and)\s*uses?|project\s+costs?|development\s+costs?|costs?|capitali[sz]ation|capital))?(?:\s+of\s+funds)?$|^(?:sources?|uses?)\s+total$/i
/** A schedule line that totals some of the lines above it ("Subtotal", "Total
 *  Closing Costs", "Total Equity"). Only a line whose amount IS that sum is
 *  read as one: a lone "Total Loan" is the loan, a component. */
const SUBTOTAL_RE = /^(?:sub[\s-]?total|total)\b/i

/** A stored schedule side: its component lines, and the total the schedule
 *  states for the side when it states one. */
export type ScheduleSide = { lines: NamedAmount[]; statedTotal: number | undefined }

/**
 * One side of a stored Sources & Uses schedule.
 *
 * The workbook reader keeps every labelled row, the schedule's own total row
 * included, so "Total Uses" arrived as one more use and was added to the very
 * lines it sums: a $63.5M refinance read $127M of uses and "Uses exceed
 * sources" (Magnolia Apartments, Chris Young, 2 Nov 2026). A stated total is
 * a total, never an addend; a subtotal is the lines it sums, never a line of
 * its own; and a line stating $0 (a template's "PREF" row) is a layer the deal
 * does not have, so it is not drawn.
 */
export function readScheduleSide(source: unknown, storedTotal?: unknown): ScheduleSide {
  // The workbook reader now keeps the side's total beside its lines
  // (`uses_total`); a payload stored before holds it as a line, read below.
  const kept =
    storedTotal && typeof storedTotal === 'object' ? num((storedTotal as Record<string, unknown>).amount) : undefined
  if (!Array.isArray(source)) return { lines: [], statedTotal: kept != null ? Math.abs(kept) : undefined }
  const lines: NamedAmount[] = []
  let statedTotal: number | undefined
  // The component lines since the last subtotal, for "is this row their sum".
  let run: number[] = []
  for (const entry of source) {
    if (!entry || typeof entry !== 'object') continue
    const row = entry as Record<string, unknown>
    const label = String(row.label ?? row.name ?? row.category ?? '').trim()
    const signed = num(row.amount ?? row.value ?? row.total)
    if (!label || signed == null) continue
    const amount = Math.abs(signed)
    const key = typeof row.key === 'string' ? row.key.trim().toLowerCase() : ''
    if (SIDE_TOTAL_RE.test(label) || key === 'total') {
      // The last one stated wins: a schedule's grand total follows its parts.
      statedTotal = amount
      run = []
      continue
    }
    if (SUBTOTAL_RE.test(label)) {
      // The sum of the run's last k lines, for any k, is a subtotal of them.
      let sum = 0
      let isSubtotal = false
      for (let i = run.length - 1; i >= 0; i -= 1) {
        sum += run[i]
        if (sameMoney(sum, amount)) {
          isSubtotal = true
          break
        }
      }
      if (isSubtotal) {
        run = []
        continue
      }
    }
    lines.push({ label, amount, readOnlyReason: STORED_SCHEDULE_REASON })
    run.push(amount)
  }
  if (kept != null) statedTotal = Math.abs(kept)
  return { lines, statedTotal: statedTotal != null && statedTotal > 0 ? statedTotal : undefined }
}

/** The total a side shows: the stated total when the schedule states one,
 *  else the sum of its lines — never both added. With both and a difference,
 *  the stated total is shown and the variance said. */
function sideTotal(side: ScheduleSide): { total: number; variance: { stated: number; lines: number; difference: number } | null } {
  const lines = side.lines.reduce((sum, row) => sum + row.amount, 0)
  if (side.statedTotal == null) return { total: lines, variance: null }
  const variance =
    side.lines.length > 0 && !sameMoney(lines, side.statedTotal)
      ? { stated: side.statedTotal, lines, difference: side.statedTotal - lines }
      : null
  return { total: side.statedTotal, variance }
}

/** A stored tranche flagged as the one the deal requests. */
function flaggedRequest(layer: Record<string, unknown>): boolean {
  return (
    isTruthyFlag(layer.isRequested) ||
    isTruthyFlag(layer.is_requested) ||
    isTruthyFlag(layer.requestedTranche) ||
    isTruthyFlag(layer.requested_tranche) ||
    isTruthyFlag(layer.requested) ||
    /\brequest(?:ed)?\b/i.test(String(layer.status ?? ''))
  )
}

function recordPosition(layer: Record<string, unknown>, index: number): string {
  return String(layer.type ?? layer.component ?? layer.name ?? layer.position ?? `Tranche ${index + 1}`)
}

/**
 * A stored row that names a debt tranche ("Senior mortgage", "Mezzanine") but
 * states no amount. `layerFromRecord` drops it — there is nothing to draw — so
 * the debt the stack records is incomplete, and a stack drawn without it put
 * the next tranche at the first dollar (a $9.6M mezzanine read 0–16.0% LTV
 * with its senior row blank). A row flagged as the request is not a gap: the
 * recorded loan amount stands in for it (an unflagged one of the request's own
 * kind is exempted where the stack is assembled).
 */
function debtRowWithoutAmount(layer: Record<string, unknown>, index: number): boolean {
  if (layerFromRecord(layer, index) != null || flaggedRequest(layer)) return false
  // A row stating $0 states that the tranche is not there; it is no gap.
  const requestedAmount = typeof layer.requested === 'boolean' ? undefined : layer.requested
  const stated = layer.amount ?? layer.approved ?? requestedAmount
  if (stated != null && stated !== '' && Number(stated) === 0) return false
  if (layer.isDebt != null) return Boolean(layer.isDebt)
  const kind = classify(recordPosition(layer, index))
  return kind === 'senior' || kind === 'mezz'
}

function layerFromRecord(layer: Record<string, unknown>, index: number): StackLayer | null {
  const requestedAmount = typeof layer.requested === 'boolean' ? undefined : layer.requested
  const amount = num(layer.amount ?? layer.approved ?? requestedAmount)
  // A $0 tranche (a template's "PREF" row left at zero) reads as no amount
  // (asNumber): not a layer the deal has, so it is not drawn.
  if (amount == null) return null
  const position = recordPosition(layer, index)
  const kind = classify(position)
  const lenderRaw = layer.lender ?? layer.investor ?? layer.party
  const term =
    layer.term != null
      ? String(layer.term)
      : layer.termMonths != null || layer.term_months != null
        ? `${layer.termMonths ?? layer.term_months} Mo.`
        : null
  const amort =
    layer.amortization != null
      ? String(layer.amortization)
      : layer.amortMonths != null || layer.amort_months != null
        ? Number(layer.amortMonths ?? layer.amort_months) === 0
          ? 'Interest Only'
          : `${Math.round(Number(layer.amortMonths ?? layer.amort_months) / 12)} YR`
        : null
  return {
    id: String(layer.id ?? `${position}-${index}`),
    kind,
    position,
    lender: lenderRaw != null && String(lenderRaw).trim() ? String(lenderRaw) : null,
    amount,
    rate: parseRate(layer.rate ?? layer.allInRate ?? layer.all_in_rate),
    rateLabel: layer.rate != null && layer.rate !== '' ? String(layer.rate) : null,
    amortization: amort,
    term,
    status: layer.status != null ? String(layer.status) : null,
    isDebt: layer.isDebt != null ? Boolean(layer.isDebt) : isDebtKind(kind),
    isImplied: false,
    isRequested:
      flaggedRequest(layer) ||
      (layer.amount == null && layer.approved == null && requestedAmount != null),
    readOnlyReason: STORED_TRANCHE_REASON,
  }
}

function formatCovenantRequirement(c: Covenant): string {
  if (c.threshold == null) return c.description ?? c.covenantType
  const scaled =
    /dscr/i.test(c.covenantType)
      ? `${c.threshold.toFixed(2)}x`
      : c.threshold <= 1.5
        ? `${(c.threshold * 100).toFixed(1)}%`
        : `${c.threshold.toFixed(1)}%`
  if (c.direction === 'max') return `≤ ${scaled}`
  return `≥ ${scaled}`
}

function currentForCovenant(c: Covenant, get: (name: string) => unknown): { display: string; value: number | undefined } {
  const type = c.covenantType.toLowerCase()
  if (type.includes('dscr')) {
    const v = num(get('dscr'))
    return { display: v == null ? '—' : `${v.toFixed(2)}x`, value: v }
  }
  if (type.includes('ltv')) {
    const v = num(get('ltv'))
    const scaled = v == null ? undefined : v <= 1.5 ? v * 100 : v
    return { display: scaled == null ? '—' : `${scaled.toFixed(1)}%`, value: scaled }
  }
  if (type.includes('debt_yield') || type.includes('debt-yield')) {
    const v = num(get('debt_yield'))
    const scaled = v == null ? undefined : v <= 1.5 ? v * 100 : v
    return { display: scaled == null ? '—' : `${scaled.toFixed(1)}%`, value: scaled }
  }
  if (type.includes('occupancy')) {
    const v = num(get('occupancy'))
    const scaled = v == null ? undefined : v <= 1.5 ? v * 100 : v
    return { display: scaled == null ? '—' : `${scaled.toFixed(1)}%`, value: scaled }
  }
  return { display: '—', value: undefined }
}

/** The synthesized future-funding tranche beside a request with no stored stack. */
const FUTURE_FUNDING_LAYER_ID = 'future-funding-recorded'

/** The uses a stack may be closed against: their total, what they are (for the
 *  disclosure), and whether they name more than the purchase price. */
type UsesBasis = { total: number; basis: string; beyondPrice: boolean }

function residualEquity(input: {
  layers: StackLayer[]
  totalEquityField: number | undefined
  propertyValue: number | undefined
  uses: UsesBasis | null
  equityDerived?: boolean
}): { layer: StackLayer; source: ResidualSource } | null {
  const debt = input.layers.filter((layer) => layer.isDebt).reduce((sum, layer) => sum + layer.amount, 0)
  const nonDebt = input.layers.filter((layer) => !layer.isDebt).reduce((sum, layer) => sum + layer.amount, 0)
  if (debt <= 0) return null
  // Value and LTV describe the property and the loan at closing. Future
  // funding is advanced later, so it is not part of either identity: implying
  // equity from "debt / LTV" with it included inflated the implied value (and
  // the equity) by the future advances divided by the LTV.
  const futureFunding = input.layers
    .filter((layer) => layer.id === FUTURE_FUNDING_LAYER_ID)
    .reduce((sum, layer) => sum + layer.amount, 0)
  const closingRecorded = debt - futureFunding + nonDebt

  // No branch reads the LTV: equity (and a total) back-solved as loan ÷ LTV
  // is the value back-solve R3 forbids, even labelled Implied (Round 3).
  let amount: number | undefined
  let source: ResidualSource | undefined
  let position = 'Implied Equity'
  // Uses are what the whole stack pays for, future advances included: a
  // capex budget is a use the future funding pays. A purchase price alone does
  // not name what the advances pay for, so a stack carrying future funding is
  // not closed against the price alone (the equity would read short by the
  // advances).
  const usesClose =
    input.uses != null &&
    (futureFunding <= 0 || input.uses.beyondPrice) &&
    input.uses.total > debt + nonDebt + 1
  if (nonDebt <= 0 && input.totalEquityField != null && input.totalEquityField > 0) {
    amount = input.totalEquityField
    source = 'equity_field'
    position = 'Sponsor Equity'
  } else if (nonDebt <= 0 && usesClose && input.uses) {
    amount = input.uses.total - debt
    source = 'uses'
  } else if (nonDebt <= 0 && input.propertyValue != null && input.propertyValue > closingRecorded + 1) {
    // Only a stack that records no equity is closed against the value: on
    // top of a stored equity tranche the gap is value uplift, which no
    // document states, and the sum read as the sponsor's stated equity
    // ("Sponsor Equity $40M" over a stored $30M and a $10M uplift).
    amount = input.propertyValue - closingRecorded
    source = 'property_value'
  }
  if (amount == null || source == null) return null
  if (!(amount > 1)) return null

  return {
    source,
    layer: {
      id: 'residual-equity',
      kind: 'equity',
      position,
      lender: null,
      amount,
      rate: undefined,
      rateLabel: null,
      amortization: null,
      term: null,
      // A total equity the engine computed (total cost − loan) is Derived,
      // not "Recorded" — the package stated none (ECON-18).
      status: source === 'equity_field' ? (input.equityDerived ? 'Derived' : 'Recorded') : 'Implied',
      isDebt: false,
      isRequested: false,
      isImplied: source !== 'equity_field',
    },
  }
}

function stackDisclosureFor(input: {
  layerCount: number
  hasRequested: boolean
  residualSource: ResidualSource | null
  equityDerived?: boolean
  equityFormula?: string | null
  propertyValueDerived?: boolean
  seniorUnstated?: { position: string; lien: string | null } | null
  debtIncomplete?: boolean
  hasLtv?: boolean
  usesBasis?: string | null
}): string {
  if (input.layerCount === 0) {
    return 'No capital stack layers are recorded. No tranches have been inferred.'
  }
  if (input.seniorUnstated) {
    const lien = input.seniorUnstated.lien ? ` (${input.seniorUnstated.lien} lien)` : ''
    return `The requested tranche is a ${input.seniorUnstated.position.toLowerCase()} note${lien}. The senior debt ahead of it is not stated on this record, so the full stack, its total and leverage through this tranche cannot be shown.`
  }
  if (input.debtIncomplete) {
    return 'A debt tranche on the stored capital stack states no amount, so the full stack, its total and each tranche’s attachment point cannot be shown.'
  }
  switch (input.residualSource) {
    case 'uses': {
      // Said with what the uses ARE: a purchase price alone leaves out the
      // closing costs and reserves an acquisition also pays, so the true
      // equity check is likely larger than this one and the reader is told.
      const basis = input.usesBasis ?? 'the stated uses'
      const priceOnly = basis === 'the purchase price'
      return `Implied equity is ${basis} less the recorded debt — the sponsor's cash no document states. It is not a stored tranche.${
        priceOnly ? ' Only the purchase price is stated as a use; closing costs, reserves or capital work would add to the equity.' : ''
      }`
    }
    case 'property_value':
      return input.propertyValueDerived
        ? 'Residual equity is the property value the platform derived — no document states it — minus recorded debt. It is not a stored tranche, and the total is implied.'
        : 'Residual equity is the recorded property value minus recorded debt — it is not a stored tranche.'
    case 'equity_field':
      return input.equityDerived
        ? `Sponsor equity is derived by the platform (${input.equityFormula ?? 'total cost − loan'}) — no document states it. It was not stored as a capital-stack tranche.`
        : 'Sponsor equity is the recorded total equity. It was not stored as a capital-stack tranche.'
    case null:
      break
    default: {
      const _exhaustive: never = input.residualSource
      return _exhaustive
    }
  }
  if (input.layerCount === 1 && input.hasLtv) {
    return 'Only the recorded requested tranche is available. No property value or equity is recorded, and a stated LTV never implies a total or an equity (rule R3), so no residual equity is shown.'
  }
  if (input.layerCount === 1) {
    return input.hasRequested
      ? 'Only the recorded requested tranche is available. LTV and property value are not on this record, so no residual equity can be shown.'
      : 'Only one recorded tranche is available. LTV and property value are not on this record, so no residual equity can be shown.'
  }
  return input.hasRequested
    ? 'The requested tranche is highlighted. Every other layer is a recorded or implied slice of the full stack.'
    : 'Every recorded tranche is shown. None is identified as the requested tranche.'
}

/** The covenant's limit and the deal's figure on the scale `formatCovenantRequirement`
 *  prints, or undefined where the covenant measures something this page has no
 *  figure for (or states no threshold). */
function covenantGauge(c: Covenant, current: number | undefined): CovenantGauge | undefined {
  if (c.threshold == null) return undefined
  const type = c.covenantType.toLowerCase()
  const metric: CovenantGauge['metric'] | null = type.includes('dscr')
    ? 'dscr'
    : type.includes('ltv')
      ? 'ltv'
      : type.includes('debt_yield') || type.includes('debt-yield')
        ? 'debt_yield'
        : type.includes('occupancy')
          ? 'occupancy'
          : null
  if (!metric) return undefined
  const limit = metric !== 'dscr' && c.threshold <= 1.5 ? c.threshold * 100 : c.threshold
  return { metric, limit, value: current, direction: c.direction === 'max' ? 'max' : 'min' }
}

function covenantCompliant(c: Covenant, current: number | undefined): boolean | null {
  if (current == null || c.threshold == null) return null
  let threshold = c.threshold
  let value = current
  const pctLike = /ltv|occupancy|debt_yield|debt-yield/i.test(c.covenantType)
  if (pctLike) {
    if (threshold <= 1.5) threshold *= 100
    if (value <= 1.5) value *= 100
  }
  if (c.direction === 'max') return value <= threshold + 1e-6
  return value >= threshold - 1e-6
}

export function buildCapitalStackModel(input: {
  get: (name: string) => unknown
  raw: RawDealFile | undefined
  structure: LoanStructure | null
  documents: DealDocument[]
  activity: LoanActivityEvent[]
  notes: { id: string; body: string; author: string; createdAt: string }[]
  /** Set when the deal's total equity is an engine-derived figure, with the
   *  formula it was derived by. */
  equityDerivation?: { formula: string | null } | null
  /** Set when the deal's property value is an engine-derived figure (a
   *  component valuation, or a back-solve not yet withdrawn): a total built
   *  on it is implied, never recorded. */
  propertyValueDerived?: boolean
}) {
  const { get, raw, structure, documents, activity, notes } = input
  const propertyValueDerived = Boolean(input.propertyValueDerived)
  const equityDerived = Boolean(input.equityDerivation)
  const cash = cashBlob(raw)
  const stackRaw = (raw?.capital_stack ?? []) as Array<Record<string, unknown>>

  let layers: StackLayer[] = stackRaw
    .map((layer, i) => layerFromRecord(layer, i))
    .filter((layer): layer is StackLayer => layer != null)

  const loanAmount = num(get('loan_amount'))
  const propertyValue = num(get('property_value'))
  const totalEquityField = num(get('total_equity'))
  const gpEquity = num(get('gp_equity'))
  const lpEquity = num(get('lp_equity'))
  const ltv = num(get('ltv'))
  const dscr = num(get('dscr'))

  // Uses first: they are what the stack pays for, so they are read before the
  // stack is closed (see the module note). Stored schedule lines win; failing
  // one, the purchase price, costs and reserves the deal records.
  const sourcesUses =
    cash.sourcesAndUses && typeof cash.sourcesAndUses === 'object'
      ? (cash.sourcesAndUses as Record<string, unknown>)
      : null
  const sourcesSide = readScheduleSide(cash.sources ?? sourcesUses?.sources, sourcesUses?.sources_total)
  const usesSide = readScheduleSide(cash.uses ?? sourcesUses?.uses, sourcesUses?.uses_total)
  const sourcesStored = sourcesSide.lines
  const usesStored = usesSide.lines
  // A schedule that states only its total (every line was $0 or a subtotal)
  // still states its uses.
  const usesStoredTotal = usesStored.length > 0 || usesSide.statedTotal != null ? sideTotal(usesSide) : null
  const uses: NamedAmount[] = [...usesStored]
  // Uses are complete only when stored, or when a purchase price or total
  // project cost anchors them. A reserve on its own is one line of a schedule
  // nobody stated, and totalling it read "Total Uses $3,400,000" against $62M
  // of sources.
  let usesComplete = usesStoredTotal != null
  const purchase = num(get('purchase_price'))
  const totalCost = num(get('total_cost'))
  if (usesStoredTotal == null) {
    // Each line says where it was read from: a catalogue field (which carries
    // its own state), the loan structure, or the cash-flow record — the last
    // two are not catalogue fields, so an unlocked dossier cannot edit them.
    const read = (name: string, structureValue: number | null | undefined, cashValue: number | undefined) => {
      const stated = num(get(name))
      if (stated != null) return { amount: stated, origin: { field: name } as Partial<NamedAmount> }
      if (structureValue != null) {
        return { amount: structureValue, origin: { readOnlyReason: 'Read from the loan structure record. No catalog field holds it, so it is not edited in place.' } }
      }
      if (cashValue != null) {
        return { amount: cashValue, origin: { readOnlyReason: 'Read from the deal’s cash-flow record. No catalog field holds it, so it is not edited in place.' } }
      }
      return null
    }
    const closing = read('closing_costs', undefined, moneyDollars(cash.closingCosts ?? cash.closing_costs))
    const reserves = read('interest_reserve', structure?.interestReserve, moneyDollars(cash.reserves))
    const capex = read('capex', structure?.capexReserve, moneyDollars(cash.capex ?? cash.capexBudget))
    if (purchase != null) uses.push({ label: 'Purchase Price', amount: purchase, field: 'purchase_price' })
    if (closing) uses.push({ label: 'Closing Costs', amount: closing.amount, ...closing.origin })
    if (reserves) uses.push({ label: 'Reserves', amount: reserves.amount, ...reserves.origin })
    if (capex) uses.push({ label: 'CapEx Budget', amount: capex.amount, ...capex.origin })
    const itemized = uses.reduce((sum, row) => sum + row.amount, 0)
    // A stated total project cost is the whole of the uses: what the lines
    // above do not itemize is the remainder, priced or not. Only with a price
    // was it added, so a construction deal (cost $60M, capex $45M, no price)
    // totalled $45M of uses and closed the stack on $5M of equity beside a
    // 66.7% LTC.
    if (totalCost != null && uses.length === 0) {
      uses.push({ label: 'Total Project Cost', amount: totalCost, field: 'total_cost' })
    } else if (totalCost != null && totalCost > itemized + 1) {
      uses.push({
        label: 'Other project costs (not itemized)',
        amount: totalCost - itemized,
        readOnlyReason: 'The stated total project cost less the lines above. Edit the total project cost or a line, not the remainder.',
      })
    }
    usesComplete = purchase != null || totalCost != null
  }
  const useTotal = usesStoredTotal != null
    ? usesStoredTotal.total
    : usesComplete ? uses.reduce((s, r) => s + r.amount, 0) : 0
  // What the uses are, in the words the disclosure uses — never a purchase
  // price the deal does not state.
  const usesBasisText =
    usesStoredTotal != null
      ? 'the stated uses'
      : uses.length === 1 && uses[0].label === 'Purchase Price'
        ? 'the purchase price'
        : totalCost != null && Math.abs(useTotal - totalCost) <= 1
          ? 'the total project cost'
          : purchase != null
            ? 'the purchase price and the costs the deal records'
            : 'the costs the deal records'
  const usesBasis: UsesBasis | null =
    usesComplete && useTotal > 0
      ? {
          total: useTotal,
          basis: usesBasisText,
          beyondPrice: usesStoredTotal != null || uses.some((row) => row.label !== 'Purchase Price'),
        }
      : null

  if (!layers.some((layer) => layer.isRequested) && loanAmount != null) {
    const matchingRequestedLayers = layers.filter(
      (layer) => layer.isDebt && sameMoney(layer.amount, loanAmount),
    )
    if (matchingRequestedLayers.length === 1) {
      const requestedId = matchingRequestedLayers[0].id
      layers = layers.map((layer) =>
        layer.id === requestedId ? { ...layer, isRequested: true } : layer,
      )
    }
  }

  // The request as a tranche, drawn in the position and lien the record
  // states (a mezzanine note is not senior debt).
  const position = stackPosition(get)
  // The loan's type names the tranche where no position is stated; the
  // Capital Program never does. A program is the client's book a deal is
  // run from ("Preferred Equity", "Mezzanine", "Program 2 — B Loans" are
  // programs), and reading it as the position drew a senior refinance as a
  // PREF tranche the deal does not have (Chris Young, 2 Nov 2026).
  const product = productField(get)?.fieldName === 'capital_program' ? null : productLabel(get)
  const rate = get('rate')
  const termMonths = get('term_months') ?? structure?.termMonths
  const amortMonths = structure?.amortMonths
  const structureAmortization =
    amortMonths == null
      ? structure?.ioMonths
        ? 'Interest Only'
        : null
      : amortMonths === 0
        ? 'Interest Only'
        : `${Math.round(amortMonths / 12)} YR`
  const label = !position.assumed
    ? position.label
    : product
      ? product
      : position.label
  const kind: LayerKind = position.assumed ? classifyOr(label, 'senior') : position.kind
  const shared = {
    kind,
    lender: null,
    rate: parseRate(rate ?? structure?.allInRate),
    rateLabel: rate != null && rate !== '' ? String(rate) : null,
    rateField: rate != null && rate !== '' ? 'rate' : undefined,
    amortization: structureAmortization ?? amortizationLabel(get),
    term: termMonths != null ? `${termMonths} Mo.` : null,
    status: null,
    isDebt: true,
    isImplied: false,
  }

  // A stored stack that neither flags nor holds the request: the request is
  // still a recorded tranche, so it is drawn beside the stored layers, after
  // the debt they record — never left off (Stanhope Row's $9.6M mezzanine
  // beside a stored $30M senior read 'Senior mortgage 100%', nd4-values-6).
  if (
    layers.length > 0 &&
    loanAmount != null &&
    loanAmount > 0 &&
    !layers.some((layer) => layer.isRequested || (layer.isDebt && sameMoney(layer.amount, loanAmount)))
  ) {
    const lastDebt = layers.reduce((at, layer, i) => (layer.isDebt ? i : at), -1)
    layers = [
      ...layers.slice(0, lastDebt + 1),
      { ...shared, id: 'requested-recorded', position: label, amount: loanAmount, isRequested: true, amountField: 'loan_amount' },
      ...layers.slice(lastDebt + 1),
    ]
  }

  // The senior debt ahead of a subordinate request is unstated whenever no
  // stored layer names a senior WITH its amount — the engine's own rule
  // (dealfields.senior_ahead_unstated), with no count of stored layers: a
  // stored mezzanine row alone, or a senior row left blank, used to skip this
  // and draw the note as first-dollar capital (Mezzanine 0–16.0% LTV) with
  // NOI over the note as its debt yield.
  //
  // That rule reads a senior row's `amount` alone, while the tranche list
  // reads a stored row by its amount, approved or requested figure
  // (layerFromRecord, as the reports' `_layer_amount` does). A senior row the
  // list prints with a figure is the senior debt this screen states: a
  // stored senior holding only `approved: 30M` read "Senior mortgage $30M"
  // beside "The senior debt ahead of it is not stated", and drew no stack.
  const seniorListed = stackRaw.some((row, index) => {
    if (row == null || typeof row !== 'object') return false
    const name = String(row.type ?? row.position ?? row.component ?? row.name ?? '')
    return /senior|first mortgage|a[\s-]?note/i.test(name) && layerFromRecord(row, index) != null
  })
  const seniorUnstated = seniorAheadUnstated(get, raw?.capital_stack) && !seniorListed
  // No stored layers: the request is the one tranche on the record, with any
  // future funding the commitment carries beside it.
  if (layers.length === 0 && loanAmount != null) {
    layers = [
      { ...shared, id: 'senior-recorded', position: label, amount: loanAmount, isRequested: true, amountField: 'loan_amount' },
    ]
    const fullyFunded = num(get('fully_funded_amount'))
    const futureFunding = num(get('future_funding'))
    const initialFunding = num(get('initial_funding'))
    // The commitment the deal RECORDS: its total, or a stated initial advance
    // plus future funding. Never the loan amount plus future funding — a term
    // sheet's "Loan Amount" is often already the whole commitment, and adding
    // the future funding on top is how The Marlowe showed an invented $27.4M
    // (package A, "never invent a commitment leg").
    const commitment = fullyFunded
      ?? (initialFunding != null && futureFunding != null ? initialFunding + futureFunding : undefined)
    if (commitment != null && commitment > loanAmount + 1) {
      layers.push({
        ...shared,
        id: FUTURE_FUNDING_LAYER_ID,
        position: `${label} — Future Funding`,
        amount: commitment - loanAmount,
        amountDerived: fullyFunded != null
          ? 'total commitment − loan amount'
          : 'initial advance + future funding − loan amount',
        amountInputs: fullyFunded != null
          ? ['fully_funded_amount', 'loan_amount']
          : ['initial_funding', 'future_funding', 'loan_amount'],
        isRequested: false,
      })
    }
  }

  // A stored debt row with no amount leaves the recorded debt incomplete:
  // no total, equity or attachment point can be drawn through the gap.
  //
  // Except the request's own row. When no stored layer holds the request, it
  // is drawn from the recorded loan amount, and one blank row of the
  // request's own kind is that tranche, left blank because the loan amount
  // holds its figure: a $9.6M mezzanine request beside a stored $30M senior
  // and a blank "Mezzanine" row drew no capitalization at all, and a plain
  // $30M senior loan with one blank "Senior Loan" row drew nothing.
  const requestDrawnFromLoan = layers.some(
    (layer) => layer.id === 'requested-recorded' || layer.id === 'senior-recorded',
  )
  let requestRowClaimed = !requestDrawnFromLoan
  const debtIncomplete = stackRaw.some((row, index) => {
    if (row == null || typeof row !== 'object' || !debtRowWithoutAmount(row, index)) return false
    if (!requestRowClaimed && classify(recordPosition(row, index)) === kind) {
      requestRowClaimed = true
      return false
    }
    return true
  })
  const stackUnknown = seniorUnstated || debtIncomplete

  const residual = stackUnknown
    ? null
    : residualEquity({
        layers,
        totalEquityField,
        propertyValue,
        uses: usesBasis,
        equityDerived,
      })
  if (residual) {
    layers = [...layers, residual.layer]
  }

  const totalStack = layers.reduce((s, l) => s + l.amount, 0)
  const totalDebt = layers.filter((l) => l.isDebt).reduce((s, l) => s + l.amount, 0)
  const totalEquityFromLayers = layers.filter((l) => !l.isDebt).reduce((s, l) => s + l.amount, 0)
  const totalEquity = totalEquityFromLayers > 0 ? totalEquityFromLayers : totalEquityField
  // Total capitalization is debt AND equity. With no equity recorded the
  // stack holds only debt, and the loan alone is not a total capitalization:
  // unknown (shown "—", no total bar), never the loan labelled as the total
  // (Senate Row: TOTAL CAPITALIZATION $67.4M beside TOTAL EQUITY —,
  // nd4-values-4).
  const equityKnown = totalEquityFromLayers > 0 || totalEquity != null
  const totalCap = stackUnknown || !equityKnown
    ? undefined
    : totalStack > 0
        ? totalStack
        : loanAmount != null && totalEquity != null
          ? loanAmount + totalEquity
          : loanAmount ?? totalEquity

  const requestedLayer = layers.find((layer) => layer.isRequested) ?? null
  const residualSource = residual?.source ?? null
  // Every figure carries its state: a total whose equity leg is implied from
  // the LTV, or built on a derived property value, is not a recorded total,
  // and an equity figure that is the implied residual is not recorded equity.
  const totalImplied =
    totalCap != null && residualSource === 'property_value' && Boolean(propertyValueDerived)
  const equityImplied = residual != null && residual.layer.isImplied && totalEquityFromLayers === residual.layer.amount
  const stackDisclosure = stackDisclosureFor({
    layerCount: layers.length,
    hasRequested: requestedLayer != null,
    residualSource,
    equityDerived,
    equityFormula: input.equityDerivation?.formula ?? null,
    propertyValueDerived,
    hasLtv: ltv != null,
    seniorUnstated: seniorUnstated && layers.length > 0
      ? { position: position.label, lien: position.lien }
      : null,
    debtIncomplete,
    usesBasis: usesBasis?.basis ?? null,
  })
  // GP + LP commitments that do not add up to the total equity are stated,
  // not reconciled here — the difference is shown, never absorbed (ECON-18).
  const committedEquity = (gpEquity ?? 0) + (lpEquity ?? 0)
  const equityCommitmentGap =
    gpEquity != null && lpEquity != null && totalEquityField != null &&
    Math.abs(committedEquity - totalEquityField) > 1
      ? totalEquityField - committedEquity
      : undefined

  // Sources assembled from the stack name each leg, and the implied equity
  // plug says it is one, by the formula of what closed it — the uses less the
  // debt, or the value less it (a refinance) — which no document states.
  const impliedFormula = impliedEquityFormula({
    residualSource: residual?.source ?? null,
    usesBasis: usesBasis?.basis ?? null,
    beyondValue: propertyValue != null && totalCap != null && totalCap > propertyValue + 1,
  })
  const impliedInputs = impliedEquityInputs({ residualSource: residual?.source ?? null, layers, uses, totalCost })
  const sources: NamedAmount[] =
    sourcesStored.length > 0
      ? sourcesStored
      : layers.map((l) => ({
          label: l.position,
          amount: l.amount,
          ...(l.isImplied ? { implied: true, formula: impliedFormula, inputs: impliedInputs } : {}),
          ...(l.amountDerived ? { formula: l.amountDerived, inputs: l.amountInputs } : {}),
          ...(l.amountField ? { field: l.amountField } : l.id === 'residual-equity' && residual?.source === 'equity_field' ? { field: 'total_equity' } : {}),
          ...(l.readOnlyReason ? { readOnlyReason: l.readOnlyReason } : {}),
        }))
  // Neither side was stored as a schedule: both are assembled from the stack
  // and the recorded costs, and say so.
  const suDerived = sourcesStored.length === 0 && usesStoredTotal == null

  // A stored schedule's total is the total it states, never that total added
  // to the lines it sums (readScheduleSide).
  const sourcesStoredTotal = sourcesStored.length > 0 ? sideTotal(sourcesSide) : null
  const sourceTotal = sourcesStoredTotal != null
    ? sourcesStoredTotal.total
    : stackUnknown
      ? 0
      : sources.reduce((s, r) => s + r.amount, 0)
  // Sources assembled from the stack carry the stack's state: a total that
  // includes an implied equity leg is implied, as the strip says (nd-020).
  const sourceTotalLabel =
    sourcesStored.length === 0 && layers.some((layer) => layer.isImplied)
      ? 'Total Sources (implied)'
      : 'Total Sources'

  const suBalance: { balanced: boolean; difference: number; text: string } | null =
    sourceTotal > 0 && useTotal > 0
      ? (() => {
          const difference = sourceTotal - useTotal
          const balanced = Math.abs(difference) <= 1
          const amount = `$${Math.round(Math.abs(difference)).toLocaleString()}`
          return {
            balanced,
            difference: Math.abs(difference),
            text: balanced
              ? 'Sources and uses balance'
              : difference > 0
                ? `Sources exceed uses by ${amount}`
                : `Uses exceed sources by ${amount}`,
          }
        })()
      : null

  // A sponsor who states their own equity, beside uses that call for a
  // different amount: both are kept and the difference is said in words —
  // the stated figure is never replaced by the plug, nor the plug hidden
  // behind "sources exceed uses" (The Marlowe: $9.5M stated against the
  // $8.5M its $33M price less a $24.5M loan calls for).
  const equityStated = totalEquity != null && !equityImplied && residualSource !== 'property_value'
  const equityByUses = usesComplete && useTotal > 0 && totalDebt > 0 ? useTotal - totalDebt : undefined
  const equityVsUses =
    equityStated && equityByUses != null && sourcesStored.length === 0 && Math.abs((totalEquity as number) - equityByUses) > 1
      ? { stated: totalEquity as number, byUses: equityByUses, difference: (totalEquity as number) - equityByUses }
      : null
  const equityPct = totalCap && totalCap > 0 && totalEquity != null ? (totalEquity / totalCap) * 100 : undefined
  const gpPct = gpEquity != null && totalEquity != null && totalEquity > 0 ? (gpEquity / totalEquity) * 100 : undefined
  const lpPct = lpEquity != null && totalEquity != null && totalEquity > 0 ? (lpEquity / totalEquity) * 100 : undefined

  // The covenants a term sheet states, in their own catalogue fields
  // (`covenant_*`): never the deal's own ratios, and never `max_ltv`, which is
  // the committee's approved maximum — a decision, not a stated covenant.
  const catalogueCovenants: CovenantRow[] = []
  const minDscr = num(get('covenant_min_dscr'))
  const maxLtv = num(get('covenant_max_ltv'))
  const minDebtYield = num(get('covenant_min_debt_yield'))
  const debtYield = num(get('debt_yield'))
  if (maxLtv != null) {
    const current = ltv == null ? undefined : ltv * 100
    const ceiling = maxLtv
    catalogueCovenants.push({
      id: 'max-ltv',
      name: 'Max LTV',
      requirement: `≤ ${ceiling.toFixed(1)}%`,
      current: current == null ? '—' : `${current.toFixed(1)}%`,
      compliant: current == null ? null : current <= ceiling + 1e-6,
      gauge: { metric: 'ltv', limit: ceiling, value: current, direction: 'max' },
    })
  }
  if (minDscr != null) {
    catalogueCovenants.push({
      id: 'min-dscr',
      name: 'Min DSCR',
      requirement: `≥ ${minDscr.toFixed(2)}x`,
      current: dscr == null ? '—' : `${dscr.toFixed(2)}x`,
      compliant: dscr == null ? null : dscr >= minDscr - 1e-6,
      gauge: { metric: 'dscr', limit: minDscr, value: dscr, direction: 'min' },
    })
  }
  if (minDebtYield != null) {
    // Both on the catalogue's 0-100 scale.
    catalogueCovenants.push({
      id: 'min-debt-yield',
      name: 'Min Debt Yield',
      requirement: `≥ ${minDebtYield.toFixed(2)}%`,
      current: debtYield == null ? '—' : `${debtYield.toFixed(2)}%`,
      compliant: debtYield == null ? null : debtYield >= minDebtYield - 1e-6,
      gauge: { metric: 'debt_yield', limit: minDebtYield, value: debtYield, direction: 'min' },
    })
  }
  const structureCovenants: CovenantRow[] = (structure?.covenants ?? [])
    .filter((c) => c.isActive)
    .map((c) => {
      const current = currentForCovenant(c, get)
      return {
        id: c.id,
        name: c.description?.trim() || c.covenantType.replace(/_/g, ' '),
        requirement: formatCovenantRequirement(c),
        current: current.display,
        compliant: covenantCompliant(c, current.value),
        gauge: covenantGauge(c, current.value),
      }
    })
  const seen = new Set(structureCovenants.map((c) => c.name.toLowerCase()))
  const covenants = [
    ...structureCovenants,
    ...catalogueCovenants.filter((c) => !seen.has(c.name.toLowerCase())),
  ]

  // Covenant limits a later email or the lender's own draft states, held for
  // a person (rule R5). Listed as proposed where the deal holds none or holds
  // a different one — never tested for compliance.
  const held = heldRevisions(raw)
  const proposedCovenants: ProposedCovenant[] = []
  const propose = (field: string, stored: number | undefined, id: string, name: string, requirement: (n: number) => string) => {
    const entry = heldFor(held, [field])
    const n = entry ? num(entry.incoming) : undefined
    if (!entry || n == null || (stored != null && Math.abs(stored - n) < 1e-9)) return
    proposedCovenants.push({ id, name, requirement: requirement(n), held: heldBasis(entry) })
  }
  propose('covenant_max_ltv', maxLtv, 'proposed-max-ltv', 'Max LTV', (n) => `≤ ${n.toFixed(1)}%`)
  propose('covenant_min_dscr', minDscr, 'proposed-min-dscr', 'Min DSCR', (n) => `≥ ${n.toFixed(2)}x`)
  propose('covenant_min_debt_yield', minDebtYield, 'proposed-min-debt-yield', 'Min Debt Yield', (n) => `≥ ${n.toFixed(2)}%`)

  const activityRows: StackActivityRow[] = []
  for (const n of notes) {
    activityRows.push({
      id: `note-${n.id}`,
      at: n.createdAt,
      title: 'Note added',
      party: n.author,
      detail: n.body.slice(0, 120),
      status: null,
    })
  }
  for (const e of activity) {
    activityRows.push({
      id: String(e.id),
      at: e.createdAt ?? '',
      title: humanizeEvent(e.eventType),
      party: e.userEmail ?? '',
      detail: '',
      status: null,
    })
  }
  activityRows.sort((a, b) => Date.parse(b.at || '') - Date.parse(a.at || ''))

  return {
    layers,
    requestedLayerId: requestedLayer?.id ?? null,
    residualSource,
    stackDisclosure,
    totalCap,
    totalImplied,
    equityImplied,
    /** The senior debt ahead of a subordinate request is not on the record. */
    seniorUnstated,
    /** A stored debt tranche states no amount, so the stack is incomplete. */
    debtIncomplete,
    // With the senior debt unstated the total debt is unknown, as the total
    // capitalization is — the note alone is not it.
    totalDebt: stackUnknown ? undefined : totalDebt > 0 ? totalDebt : loanAmount,
    totalEquity,
    totalLoan: totalDebt > 0 ? totalDebt : loanAmount,
    /** The loan an LTV is measured on — the deal's loan amount, as the
     *  Overview and the derivation (loan / value) measure it (nd-005). */
    ltvLoanAmount: loanAmount,
    ltv,
    dscr,
    equityPct,
    gpEquity,
    lpEquity,
    gpPct,
    lpPct,
    equityDerived,
    equityCommitmentGap,
    /** A stated equity that differs from what the stated uses less the debt
     *  call for, with both figures (null when they agree or one is unknown). */
    equityVsUses,
    /** The purchase price and total project cost the uses were read from. */
    purchasePrice: purchase,
    totalCost,
    /** What the stated uses are, as the disclosure names them. */
    usesBasis: usesBasis?.basis ?? null,
    suTemplate: typeof sourcesUses?.template === 'string' && sourcesUses.template.trim()
      ? sourcesUses.template.trim()
      : null,
    sources,
    uses,
    sourceTotal: sourceTotal > 0 ? sourceTotal : undefined,
    sourceTotalLabel,
    useTotal: useTotal > 0 ? useTotal : undefined,
    usesComplete,
    suDerived,
    /** A stored schedule that states a side's total AND lines that do not add
     *  up to it: the stated total is shown, and the difference said — never
     *  absorbed and never added (null when they agree or one is missing). */
    usesVariance: usesStoredTotal?.variance ?? null,
    /** The side totals the stored schedule states, read as stated. */
    usesStatedTotal: usesSide.statedTotal != null && usesStoredTotal != null,
    sourcesStatedTotal: sourcesSide.statedTotal != null && sourcesStoredTotal != null,
    sourcesVariance: sourcesStoredTotal?.variance ?? null,
    suBalance,
    covenants,
    proposedCovenants,
    activity: activityRows.slice(0, 8),
    documents: documents.slice(0, 8),
    propertyValue,
  }
}

export type CapitalStackModel = ReturnType<typeof buildCapitalStackModel>

/** A rate the record states in words is already a rate.
 *
 *  A term sheet says "SOFR + 350" — an index plus a spread in basis points —
 *  and appending a percent sign to it produced "SOFR + 350%", a rate off by
 *  two orders of magnitude on a page a credit reader prices from. Only a bare
 *  number is missing its unit. */
export function rateLabel(rate: number | undefined, fallback: string | null): string {
  const stated = fallback?.trim()
  if (stated) {
    return /^[\d.,\s]+$/.test(stated) ? `${stated}%` : stated
  }
  if (rate == null) return '—'
  return `${rate.toFixed(2)}%`
}
